import React, { useEffect, useRef } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { verifyEmailOnce } from '../../utils/emailVerificationOnce';

/**
 * Catches Appwrite verification links on any route (/, /login, old templates)
 * so updateVerification runs before the user signs in and invalidates the secret.
 */
const AppEmailVerificationGate: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const handledRef = useRef<string | null>(null);

  const userId = searchParams.get('userId');
  const secret = searchParams.get('secret');

  useEffect(() => {
    if (!userId || !secret) return;
    if (location.pathname === '/verify-email') return;

    const key = `${userId}:${secret}`;
    if (handledRef.current === key) return;
    handledRef.current = key;

    let cancelled = false;
    (async () => {
      try {
        await verifyEmailOnce(userId, secret);
        if (!cancelled) {
          navigate('/login?verified=1', { replace: true });
        }
      } catch {
        if (!cancelled) {
          const qs = new URLSearchParams({ userId, secret });
          navigate(`/verify-email?${qs.toString()}`, { replace: true });
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [location.pathname, navigate, secret, userId]);

  return <>{children}</>;
};

export default AppEmailVerificationGate;
